import Bugsnag from '@bugsnag/js';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {OPTIONS_FILTER, POPUP_CONFIG} from 'constants/message';
import { COOKIE_CALLBACK_NUMBER } from 'constants/env';
import { requestOTPAPI } from 'modules/Api/auth';
import { COOKIE_EXPIRY_TIME } from 'store/Config';

export const formatTime = totalSeconds => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const getCookie = key => {
    try {
        const cookies = document.cookie.split('; ').reduce((acc, cookie) => {
            const [k, v] = cookie.split('=');
            acc[k] = v;
            return acc;
        }, {});
        return cookies[key] || null;
    } catch (err) {
        Bugsnag.notify('Error getting cookie: ', err);
        return null;
    }
};

export const setCookie = (key, value, maxAgeSeconds) => {
    try {
        let cookieStr = `${key}=${value}; path=/; SameSite=Lax`;
        if (Number.isFinite(maxAgeSeconds)) {
            cookieStr += `; max-age=${maxAgeSeconds}`;
        }
        if (location.protocol === 'https:') {
            cookieStr += '; Secure';
        }
        document.cookie = cookieStr;
    } catch (err) {
        Bugsnag.notify('Error setting cookie: ', err);
    }
};

export const deleteCookie = key => {
    document.cookie = `${key}=; max-age=0; path=/; SameSite=Lax`;
};

export const extractParams = pathname => {
    try {
        const [rawPId, rawRId] = pathname.split('/').filter(Boolean);

        return {
            partnerId: rawPId?.startsWith('p') ? rawPId : undefined,
            requestId: rawRId?.startsWith('r') ? rawRId : undefined,
        };
    } catch (err) {
        Bugsnag.notify('Error extracting params from URL: ', err);
        return { partnerId: undefined, requestId: undefined };
    }
};

export const addParamsToUrl = (url, params) => {
    try {
        const urlObj = new URL(url);

        // Filter out undefined values
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined)
        );

        urlObj.search = new URLSearchParams(filteredParams).toString();
        return urlObj.toString();
    } catch (err) {
        Bugsnag.notify(err, {
            context: 'Error constructing URL with params',
            metadata: { url, params },
        });
        return url;
    }
};

export const convertToLocalPhoneFormat = phoneNumber => {
    try {
        const cleanedNumber = phoneNumber.replace(/\+1|\D/g, '');

        if (cleanedNumber.length !== 10) {
            throw new Error(
                'Invalid phone number. Must be 10 digits after country code.'
            );
        }

        const formattedNumber = cleanedNumber.replace(
            /(\d{3})(\d{3})(\d{4})/,
            '$1-$2-$3'
        );
        return formattedNumber;
    } catch (err) {
        Bugsnag.notify('Error formatting phone number: ', err);
        return '';
    }
};

export const showErrorToast = (message = 'Something went wrong') => {
    toast.dismiss();
    toast.error(message);
};

export const sendOTPAndRedirect = async ({
    setIsLoading,
    access_token,
    phoneNumber,
    partnerId,
    requestId,
    navigate,
    t,
    resendInterval,
}) => {
    try {
        setIsLoading(true);
        if (!requestId || !getCookie(`otp-sent-${requestId}`)) {
            await requestOTPAPI({
                phoneNumber,
                isRefresh: false,
                access_token,
                partnerId,
                requestId,
                resendInterval,
            });
            Bugsnag.leaveBreadcrumb('OTP requested successfully.');
        }
        navigate(
            requestId ? `/${partnerId}/${requestId}/otp` : `/${partnerId}/otp`
        );
    } catch (error) {
        if (requestId) {
            navigate(`/${partnerId}/${requestId}/optin`);
        } else {
            navigate(`/${partnerId}/optin`);
        }
        if (error?.response?.data?.key) {
            showErrorToast(t(error.response.data.key));
        } else if (
            error?.response?.data?.data &&
            Object.keys(error.response.data.data).length > 0
        ) {
            showErrorToast(t(Object.keys(error.response.data.data)[0]));
        } else {
            Bugsnag.notify('Error sending OTP: ', error);
            navigate('/error/server_error');
        }
    } finally {
        setIsLoading(false);
    }
};

export const isAuthenticated = callBackNumber => {
    try {
        const storedCBNumber = getCookie(COOKIE_CALLBACK_NUMBER);
        if (storedCBNumber && normalizeNumber(storedCBNumber) === normalizeNumber(callBackNumber)) {
            return true;
        }
        return false;
    } catch (err) {
        Bugsnag.notify('Error checking authentication: ', err);
        return false;
    }
};

export const fetchInitialData = async ({
    partnerId,
    requestId,
    setIsLoading,
    fetchData,
    updateUserData,
    setNewUserData,
    navigate,
    t,
    setFilterNonurgent,
    skipRedirect = false,
    setHidePopup,
    setWMAAllowed,
    setEnableContinueBtn,
    setShowContinueBtn,
}) => {
    try {
        setIsLoading(true);
        const partnerData = await fetchData({ partnerId, requestId });
        const migratedId = partnerData?.partner?.migrated_partner_short_id || null;
        if (migratedId && migratedId !== partnerId && typeof window !== 'undefined') {
            const segments = window.location.pathname.split('/').filter(Boolean);
            if (segments.length > 0) {
                segments[0] = migratedId;
            } else {
                segments.push(migratedId);
            }
            const newPath =
                `/${segments.join('/')}` +
                (window.location.search || '') +
                (window.location.hash || '');
            window.location.replace(newPath);
            return;
        }
        updateUserData({ requestId });
        setNewUserData(partnerData);
        const callBackNumber = partnerData?.phoneNumber || '';
        const accessToken = partnerData?.access_token || '';
        const isOptedIn = partnerData?.call_detail?.optin_status || 'false';
        const isCallSubmitted = 
            partnerData?.call_detail?.is_call_submitted || false;
        const wmaReplyEnabled = partnerData?.partner?.wma_enabled || false;
        const wmaSubmitEnabled =
            partnerData?.partner?.wma_settings?.wma_submit || false;
        const wmaUrgentReplyAllowed = isWMAUrgentReplyAllowed(partnerData?.partner);
        Bugsnag.leaveBreadcrumb('Bootstrap data fetched successfully.');

        if (skipRedirect) {
            return;
        }

        if( (!wmaReplyEnabled && !wmaSubmitEnabled)) {
            setHidePopup(false);
            setWMAAllowed(POPUP_CONFIG.SUBMIT_WORKFLOW_DISABLED);
            setShowContinueBtn(false);
            return navigate(`/${partnerId}/home`);
        }

        if ( (!requestId) || (requestId && !isCallSubmitted) ) {    
            if (wmaUrgentReplyAllowed === OPTIONS_FILTER.SUBMIT_WORKFLOW_DISABLED) {
                setHidePopup(false);
                setWMAAllowed(POPUP_CONFIG.SUBMIT_WORKFLOW_DISABLED);
                setShowContinueBtn(false);
                return navigate(`/${partnerId}/home`);
            }
        }

        if (partnerData?.max_dob_attempt_exceeded) {
            return navigate('/error/expired_link');
        }
        if (!requestId) {
            const storedSessionStr = getCookie('storedPartnerURLSession');
            const storedSession = storedSessionStr ? JSON.parse(storedSessionStr) : null;
            if (storedSession) {
                if (storedSession.partnerId !== partnerId) {
                    if (wmaUrgentReplyAllowed === OPTIONS_FILTER.BLOCKED) {                   
                        setHidePopup(false);
                        setWMAAllowed(POPUP_CONFIG.BLOCKED);
                        setEnableContinueBtn(false);
                    }
                    return navigate(`/${partnerId}/home`);
                }
                if (storedSession.requestId) {
                    return navigate(`/${partnerId}/optin`);
                }
                if (storedSession.authenticated) {
                    updateUserData({ phoneNumber: storedSession.phoneNumber});
                    const shouldShowPopup = wmaUrgentReplyAllowed === OPTIONS_FILTER.BLOCKED ||
                        wmaUrgentReplyAllowed === OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED;

                    if (shouldShowPopup) {
                        if (wmaUrgentReplyAllowed === OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED) {
                            setFilterNonurgent(true);
                            setWMAAllowed(POPUP_CONFIG.NON_URGENT_ALLOWED);
                        } else {
                            setHidePopup(false);
                            setWMAAllowed(POPUP_CONFIG.BLOCKED);
                            setEnableContinueBtn(false);
                            return navigate(`/${partnerId}/home`);
                        }
                        setHidePopup(false);
                    }
                    return navigate(`/${partnerId}/${requestId ? `${requestId}/` : ''}wma-map`);
                }
                if (storedSession.optin) {
                    updateUserData({ phoneNumber: storedSession.phoneNumber});
                    return navigate(`/${partnerId}/${requestId ? `${requestId}/` : ''}otp`);
                }
            }
            if (wmaUrgentReplyAllowed === OPTIONS_FILTER.BLOCKED) {                   
                setHidePopup(false);
                setWMAAllowed(POPUP_CONFIG.BLOCKED);
                setEnableContinueBtn(false);
            }
            return navigate(`/${partnerId}/home`);
        }
        if(!isCallSubmitted){
            if (wmaUrgentReplyAllowed === OPTIONS_FILTER.BLOCKED) {                   
                setHidePopup(false);
                setWMAAllowed(POPUP_CONFIG.BLOCKED);
                setEnableContinueBtn(false);
                return navigate(`/${partnerId}${requestId?`/${requestId}`:''}/home`);
            }
            const storedSessionStr = getCookie('storedPartnerURLSession');
            const storedSession = storedSessionStr ? JSON.parse(storedSessionStr) : null;
            if (storedSession) {
                if (storedSession.phoneNumber === partnerData?.phoneNumber.replace('+1', '').replace(/-/g, '')) {
                    setCookie('storedPartnerURLSession', JSON.stringify({
                        ...storedSession,
                        requestId,
                    }), COOKIE_EXPIRY_TIME);
                }
            }
        }
        if (isOptedIn === 'false') {
            return navigate(`/${partnerId}${requestId ? `/${requestId}` : ''}/optin`);
        }

        if (!isAuthenticated(callBackNumber)) {
            return await sendOTPAndRedirect({
                setIsLoading,
                access_token: accessToken,
                phoneNumber: callBackNumber,
                partnerId,
                requestId,
                navigate,
                t,
            });
        }
        if (!isCallSubmitted) {
            const shouldShowPopup = wmaUrgentReplyAllowed === OPTIONS_FILTER.BLOCKED || 
                                   wmaUrgentReplyAllowed === OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED;
            
            if (shouldShowPopup) {
                if(wmaUrgentReplyAllowed === OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED){
                    setWMAAllowed(POPUP_CONFIG.NON_URGENT_ALLOWED);
                }else{
                    setHidePopup(false);
                    setWMAAllowed(POPUP_CONFIG.BLOCKED);
                    setEnableContinueBtn(false);
                    return navigate(`/${partnerId}/home`);
                }
                setHidePopup(false);
                return navigate(`/${partnerId}/${requestId}/wma-map`);
            }else{
                return navigate(`/${partnerId}/${requestId}/wma-map`);
            }
        }

        return navigate(`/${partnerId}/${requestId}/reply`);
    } catch (err) {
        const status = Number(err);
        switch (status) {
            case 500:
                navigate('/error/server_error');
                break;
            case 422:
                updateUserData({ requestId: null });
                window.location.replace(`/${partnerId}/home`);
                break;
            default:
                Bugsnag.notify('Error during initial data fetch: ', err);
                navigate('/error/invalid_url');
        }
    } finally {
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
    }
};

export const isWMAUrgentReplyAllowed = partner => {
    const {
        timezone,
        wma_settings,
        office_hours,
        application_type,
    } = partner;

    if (!timezone) {
        return OPTIONS_FILTER.BLOCKED;
    }

    if ( !office_hours || office_hours.length === 0 || office_hours.every(o => !o?.ends_at && !o?.starts_at) || !wma_settings?.wma_submit) {
            return OPTIONS_FILTER.SUBMIT_WORKFLOW_DISABLED;
    }

    const nowInPartnerTZ = moment().tz(timezone).format('HH:mm');
    const today = moment().tz(timezone).format('dddd').toLowerCase();

    const todayOffice = office_hours.find(
        o => o.day_name.toLowerCase() === today
    );
    
    if (!todayOffice) {
        return OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED;
    }

    const start = todayOffice.starts_at;
    const end = todayOffice.ends_at;

    const isOfficeHours = nowInPartnerTZ >= start && nowInPartnerTZ <= end;
    
    if(application_type?.includes('daytime') && application_type?.includes('afterhours')) {
        return OPTIONS_FILTER.ALL_ALLOWED;
    }

    if (application_type?.includes('daytime') && !application_type?.includes('afterhours')) {
        if (isOfficeHours && todayOffice?.is_open) {
            return OPTIONS_FILTER.ALL_ALLOWED;
        } else {
            if (wma_settings?.wma_after_hours_allowed) {
                return OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED;
            } else {
                return OPTIONS_FILTER.BLOCKED;
            }
        }
    }
    if (application_type?.includes('afterhours') && !application_type?.includes('daytime')) {
        if (!isOfficeHours || !todayOffice?.is_open) {
            return OPTIONS_FILTER.ALL_ALLOWED;
        } else {
            if (wma_settings?.wma_office_hours_allowed) {
                return OPTIONS_FILTER.ONLY_NON_URGENT_ALLOWED;
            } else {
                return OPTIONS_FILTER.BLOCKED;
            }
        }
    }
};

export const formatPhoneNumber = (value = '') => {
    let digits = value.replace(/\D/g, '');
    if (digits.startsWith('1') && digits.length === 11) {
        digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const capitalizeFirstLetter = (value = '') => {
    if (typeof value !== 'string' || value.length === 0) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

export const removeLeadingPlusOneFromPhoneNumber = (phoneNumber = '') => {
    return phoneNumber.replace(/^\+1[-\s]?/, '');
}

export const addCountryCode = number => {
    if (!number) return '';

    let digits = number.replace(/\D/g, '');

    if (digits.startsWith('1') && digits.length > 10) {
        return `+${digits}`;
    }
    return `+1${digits}`;
}

export const formatDateToYMD = dob => {
    if (!dob) return '';
    const [month, day, year] = dob.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

function normalizeNumber(number = '') {
	return number
		.replace(/\D/g, '')   // remove all non-digit characters
		.replace(/^1/, '');   // remove leading 1 (US country code)
}
